import { ObjectHelper } from '@barthauer/abc/core';

export interface BCEObject {
  Id: string;
  Designation: string;
  Description: string;
  SysDateUpdate?: string;
  SysUserNameUpdate?: string;
  Childs?: BCEElement[];
}

export class BCEGroup implements BCEObject {
  Id: string;
  Designation: string;
  Description: string;
  Childs: BCEElement[] = [];
  constructor(data) {
    this.Id = data.Id;
    this.Designation = data.Designation;
    this.Description = data.Description;
    BCEGroup.appendChilds(this, data.Nodes, '');
  }

  private static appendChilds(target: BCEObject, nodes: any[], parentId: string): void {
    for (const node of ObjectHelper.asArray(nodes)) {
      let nodeParentId = node?.IdRefMElementParent;
      if (nodeParentId === node.Id) {
        nodeParentId = '';
      }
      if ((nodeParentId ?? '') === parentId) {
        let bceObject: BCEElement;
        if (node.Type === 'Folder') {
          bceObject = new BCEFolder(node);
        } else if (node.Type === 'Document') {
          bceObject = new BCEDocument(node);
        } else if (node.Type === 'Template') {
          bceObject = new BCETemplate(node);
        }
        if (!target.Childs) {
          target.Childs = [];
        }
        target.Childs.push(bceObject);
        this.appendChilds(bceObject, nodes, bceObject.Id);
      }
    }
  }

  public static getGroupId(documentType: BCEDocumentType): string {
    switch (documentType) {
      case BCEDocumentType.OPathQueryDocument:
        return BCEGroupPacket.GROUP_ID_OPATHQUERIES;
      case BCEDocumentType.OPathReportDocument:
        return BCEGroupPacket.GROUP_ID_OPATHREPORTS;
      case BCEDocumentType.ArenaDefinitionDocument:
        return BCEGroupPacket.GROUP_ID_ARENADEFINITIONS;
      case BCEDocumentType.DashboardDocument:
        return BCEGroupPacket.GROUP_ID_DASHBOARDS;
      case BCEDocumentType.GraphicsConfigDocument:
        return BCEGroupPacket.GROUP_ID_GRAPHICSCONFIG;
      case BCEDocumentType.DWAM150ExportWizardSettingsDocument:
        return BCEGroupPacket.GROUP_ID_M150CONFIG;
      case BCEDocumentType.AsciiImportDocument:
        return BCEGroupPacket.GROUP_ID_ASCII;
      default:
        throw new Error();
    }
  }

  public static getFolderId(documentType: BCEDocumentType): string {
    switch (documentType) {
      case BCEDocumentType.DWAM150ExportWizardSettingsDocument:
        return BCEGroupPacket.FOLDER_ID_DWAM150SETTINGS;
      default:
        return '';
    }
  }

  public static getActiveX(documentType: BCEDocumentType): string {
    switch (documentType) {
      case BCEDocumentType.OPathQueryDocument:
        return BCEGroupPacket.ACTIVEX_OPATHQUERIES;
      default:
        return '';
    }
  }

  public static getDocumentTypeByActiveX(activeX: string): BCEDocumentType {
    if (!activeX) {
      return undefined;
    }
    switch (activeX) {
      case BCEGroupPacket.ACTIVEX_OPATHQUERIES:
        return BCEDocumentType.OPathQueryDocument;
      default:
        break;
    }
  }
}

export abstract class BCEElement implements BCEObject {
  Id: string;
  Designation: string;
  Description: string;
  SysDateUpdate: string;
  SysUserNameUpdate: string;
  Childs: BCEElement[];
  constructor(data) {
    this.Id = data.Id;
    this.Designation = data.Designation;
    this.Description = data.Description;
    this.SysDateUpdate = data.SysDateUpdate;
    this.SysUserNameUpdate = data.SysUserNameUpdate;
  }
}

export class BCEFolder extends BCEElement {
  constructor(data) {
    super(data);
    this.Childs = [];
  }
}

export abstract class BCEDocumentBase extends BCEElement {
  ActiveX: string;
  constructor(data) {
    super(data);
    this.ActiveX = data.ActiveX;
  }

  public get documentType(): BCEDocumentType {
    return BCEGroup.getDocumentTypeByActiveX(this.ActiveX);
  }
}

export class BCEDocument extends BCEDocumentBase {
  constructor(data) {
    super(data);
  }
}

export class BCETemplate extends BCEDocumentBase {
  constructor(data) {
    super(data);
  }
}

export enum BCEDocumentType {
  OPathQueryDocument = 'OPathQueryDocument',
  OPathReportDocument = 'OPathReportDocument',
  ArenaDefinitionDocument = 'ArenaDefinitionDocument',
  DashboardDocument = 'DashboardDocument',
  GraphicsConfigDocument = 'GraphicsConfigDocument',
  DWAM150ExportWizardSettingsDocument = 'DWAM150ExportWizardSettingsDocument',
  AsciiImportDocument = 'AsciiImportDocument',
}

export enum BCEGroupPacket {
  GROUP_ID_OPATHQUERIES = '66549C4D-1CA2-4062-8837-5ADE3C318FAE',
  GROUP_ID_OPATHREPORTS = '16F84B33-90DE-45CA-8EF7-0F0334A70303',
  GROUP_ID_ARENADEFINITIONS = '0B0E8259-1CB8-4141-8D37-3771ADBD28A6',
  GROUP_ID_DASHBOARDS = '96331CEC-424F-4F52-AAFB-D98794131832',
  GROUP_ID_ASCII = 'ECBAF654-F997-47B8-9BD6-DE67CE5C8F5A',

  GROUP_ID_QUERYS = '0B39555E-8109-44C0-85CD-C71C947B5D88',
  GROUP_ID_MODELS = '1B70E6E7-1E75-481D-8074-3ACA1C89EC51',
  GROUP_ID_MODELS9 = '8E1F7C47-8D33-474B-9C9B-2EF3FCFC2307',
  GROUP_ID_SURVEYPOINTS = '34710852-B804-401A-AD8A-AE3C0AE4A8AD',
  GROUP_ID_PRESETTINGS = '55A00D7C-81E8-4F6D-B0F0-851C105404D5',
  GROUP_ID_LEGENDDEFINITIONS = '769203A2-C492-40AC-9018-46C6832A51EA',
  GROUP_ID_LONGDITUNALSECTIONS = 'A2E9657F-77EB-41F6-A886-64C648FA3100',
  GROUP_ID_REPORTS = 'C44C700C-B0BB-4147-A649-F56BD224BE7E',
  GROUP_ID_TABREPLACE = 'E1E25084-7D63-4E0D-B52D-FE9A3E31CBDF',
  GROUP_ID_UNIWERT = '8749D720-103A-47B4-B1FF-9E774653CB10',
  GROUP_ID_VISOR = '17721440-3101-4638-B9AD-215DC4177342',
  GROUP_ID_MODELDESIGNER = '8E1F7C47-8D33-474B-9C9B-2EF3FCFC2307',
  GROUP_ID_COSTESTIMATION = 'D619D4B3-BEAE-462D-ACFA-529C55CD6B0E',
  GROUP_ID_MAILMERGE = '5C459F09-7E4A-4B18-ADC6-3CB8CE394DE9',
  GROUP_ID_ARENALAYOUTS = '5748A259-ACBA-40D9-A44D-0342329B1E19',
  GROUP_ID_ARCGISSEARCH = '29046F6D-1187-4A1F-8A69-9A72C8A4A951',
  GROUP_ID_DBSCHEMACONFIG = 'EBBC23E0-5302-424B-B640-69A3F31F84F1',
  GROUP_ID_GRAPHICSCONFIG = '663BBA10-06EF-4932-8494-B113904EF15F',
  GROUP_ID_GRAPHICSPRESETTINGS = '84EC1136-7FC0-44A1-8A92-FCABC4E9AF13',
  GROUP_ID_ARENAPROFILES = 'B5693B46-4308-4868-AD36-BF384B91A29D',
  GROUP_ID_MOBILECONFIG = 'FDCDEA7D-FD24-4DAD-B4CC-7D2907AEF6AA',
  GROUP_ID_M150CONFIG = '97668025-519E-43BA-ACF6-F51D718A43DA',
  GROUP_ID_GISINTERSECTION = '92B604FA-E10C-49F2-856A-C4DA82A0AE67',
  GROUP_ID_DATATRANSFORMATION = '869B259C-6E67-4050-940B-D3D23122CAA2',
  GROUP_ID_DMS = 'E8F508AA-C76B-4DE3-B2CD-5E2E65B40854',
  GROUP_ID_WORKFLOWS = '8AE93383-A2C4-49F8-8300-340CD6857FD2',
  GROUP_ID_ECONOMICCOMPARISON = '9BE391AC-210F-4D21-A4B0-3995BF4AB000',
  GROUP_ID_SPREADSHEET = 'CD054B06-1FE1-4D0D-B46A-8DAEDA0D5DF3',
  GROUP_ID_APPCONFIG = '8CD47EC1-42E6-420F-8E97-5C85CB3DBFDE',
  GROUP_ID_PROFILESETTINGS = '7921AECD-A858-49E9-B984-1704EBB07D8A',

  FOLDER_ID_DWAM150SETTINGS = 'B38268A6-7757-48A9-BF38-E3AF449C4ADA',

  ACTIVEX_OPATHQUERIES = 'DotNet: Barthauer.Applications.BaSYS.Management.ConfigurationExplorer.Adapters.Common.OXE.OXEDesigner, Barthauer.Applications.BaSYS.Management.ConfigurationExplorer.Adapters.Common.OXE',
}

export enum BCEOpenMode {
  Edit = 'Edit',
  Execute = 'Execute',
}

export enum BCETypeEnum {
  Group,
  Folder,
  Document,
  Template,
}
